import {
  type CrowdexUIMessagePart,
  isCodeModeTool,
  toolImageCallId,
} from '@crowdex/agent-contracts';
import { basename } from '@/lib/desktop-paths';
import type { DynamicToolPart, FilePart } from './types';

/**
 * Code mode's steps (`crowdex_run`): a cell of JavaScript whose globals are the conversation's
 * tools. A cell is named by what its code calls ("List avatars, then show Avatars"), and each
 * operation it runs is a step of its own that code mode writes after the cell (`avatars.avatar_list`,
 * its call id `code-mode-…`): those fold into their cell.
 */

/** What an operation does, as the turn's steps count it. */
export type CodeCallKind = 'read' | 'show' | 'browser' | 'write';

/** One operation a cell calls: `namespace.method(args)`, its literal string arguments. */
export type CodeCall = {
  namespace: string;
  method: string;
  args: Readonly<Record<string, string>>;
  /** The human title its server gives it, when it gives one. */
  title?: string | undefined;
  /** What its server says of it: it only reads (true), or not said. */
  readOnly?: boolean | undefined;
};

/** A cell's step: `crowdex_run` under each agent's name (`mcp__<server>__crowdex_run`, …). */
export function isCodeRunPart(part: CrowdexUIMessagePart): boolean {
  if (part.type !== 'dynamic-tool') return false;
  const named = (value: string | undefined) =>
    value !== undefined && isCodeModeTool(value) && /crowdex_run/i.test(value);
  return named(part.toolName) || named(part.title);
}

/** An operation code mode wrote for a cell (`code-mode-…`, named `<namespace>.<tool>`). */
export function isCodeCallPart(part: CrowdexUIMessagePart): boolean {
  return (
    part.type === 'dynamic-tool' &&
    part.toolCallId.startsWith('code-mode-') &&
    /^[A-Za-z_$][\w$]*\.[A-Za-z_$][\w$]*$/.test(part.toolName)
  );
}

/** The code of a cell, as its agent sent it (`{ code }`). */
function codeRunCode(part: DynamicToolPart): string | null {
  const input = part.input as { code?: unknown } | null | undefined;
  return typeof input?.code === 'string' ? input.code : null;
}

function stringArgs(input: unknown): Record<string, string> {
  if (typeof input !== 'object' || input === null) return {};
  return Object.fromEntries(
    Object.entries(input).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
}

/** The operation a code-mode step is. */
function codeCallOfPart(part: DynamicToolPart): CodeCall {
  const [namespace = '', method = ''] = part.toolName.split('.');
  const metadata = (part as { toolMetadata?: { crowdex?: { readOnly?: unknown } } }).toolMetadata;
  const title = part.title?.trim();
  return {
    namespace,
    method,
    args: stringArgs(part.input),
    ...(title === undefined || title === '' || title === part.toolName ? {} : { title }),
    ...(metadata?.crowdex?.readOnly === true ? { readOnly: true } : {}),
  };
}

/**
 * The code with its strings, template literals and comments blanked out (same length), so what
 * they say is never read as a call.
 */
function maskCode(code: string): string {
  const out = code.split('');
  const blank = (from: number, to: number) => {
    for (let index = from; index < to; index += 1) if (out[index] !== '\n') out[index] = ' ';
  };
  let index = 0;
  while (index < code.length) {
    const char = code[index] as string;
    const next = code[index + 1];
    if (char === '/' && next === '/') {
      const end = code.indexOf('\n', index);
      const stop = end === -1 ? code.length : end;
      blank(index, stop);
      index = stop;
    } else if (char === '/' && next === '*') {
      const end = code.indexOf('*/', index + 2);
      const stop = end === -1 ? code.length : end + 2;
      blank(index, stop);
      index = stop;
    } else if (char === "'" || char === '"' || char === '`') {
      let cursor = index + 1;
      while (cursor < code.length && code[cursor] !== char) {
        if (code[cursor] === '\\') cursor += 1;
        if (char !== '`' && code[cursor] === '\n') break;
        cursor += 1;
      }
      blank(index + 1, Math.min(cursor, code.length));
      index = cursor + 1;
    } else index += 1;
  }
  return out.join('');
}

/** Where the bracket opened at `open` closes in masked code, or -1. */
function closingBracket(masked: string, open: number): number {
  const pairs: Record<string, string> = { '(': ')', '[': ']', '{': '}' };
  const stack: string[] = [];
  for (let index = open; index < masked.length; index += 1) {
    const char = masked[index] as string;
    if (char in pairs) stack.push(pairs[char] as string);
    else if (char === stack.at(-1)) {
      stack.pop();
      if (stack.length === 0) return index;
    }
  }
  return -1;
}

/** Names the code declares itself (variables, destructured names, parameters). */
function declaredNames(masked: string): Set<string> {
  const names = new Set<string>();
  const addPattern = (pattern: string) => {
    // In `{ key: alias = 1 }` only `alias` is declared; a bare `name` is.
    for (const match of pattern.matchAll(/([A-Za-z_$][\w$]*)\s*(:)?/g)) {
      if (match[2] === undefined) names.add(match[1] as string);
    }
  };
  for (const match of masked.matchAll(/\b(?:const|let|var)\s+([A-Za-z_$][\w$]*)/g))
    names.add(match[1] as string);
  for (const match of masked.matchAll(/\b(?:const|let|var)\s*([{[])/g)) {
    const open = (match.index ?? 0) + match[0].length - 1;
    const close = closingBracket(masked, open);
    if (close !== -1) addPattern(masked.slice(open + 1, close).replace(/=[^,}\]]*/g, ''));
  }
  for (const match of masked.matchAll(/\(([^()]*)\)\s*=>/g))
    addPattern((match[1] as string).replace(/=[^,]*/g, ''));
  for (const match of masked.matchAll(/([A-Za-z_$][\w$]*)\s*=>/g)) names.add(match[1] as string);
  for (const match of masked.matchAll(/\bfunction\b\s*([A-Za-z_$][\w$]*)?\s*\(([^)]*)\)/g)) {
    if (match[1] !== undefined) names.add(match[1]);
    addPattern((match[2] as string).replace(/=[^,]*/g, ''));
  }
  for (const match of masked.matchAll(/\bcatch\s*\(\s*([A-Za-z_$][\w$]*)/g))
    names.add(match[1] as string);
  return names;
}

/** Globals of the interpreter's language, not the conversation's tools. */
const BUILTINS = new Set([
  'Array',
  'Boolean',
  'Buffer',
  'Date',
  'Error',
  'Intl',
  'JSON',
  'Map',
  'Math',
  'Number',
  'Object',
  'Promise',
  'Reflect',
  'RegExp',
  'Set',
  'String',
  'Symbol',
  'URL',
  'URLSearchParams',
  'console',
  'crypto',
  'document',
  'globalThis',
  'process',
  'super',
  'this',
  'window',
]);

/** Methods of a value (a list, a string, a promise) rather than tools. */
const VALUE_METHODS = new Set([
  'at',
  'catch',
  'concat',
  'entries',
  'every',
  'filter',
  'finally',
  'find',
  'findIndex',
  'flat',
  'flatMap',
  'forEach',
  'includes',
  'indexOf',
  'join',
  'keys',
  'map',
  'push',
  'reduce',
  'replace',
  'slice',
  'some',
  'sort',
  'split',
  'startsWith',
  'endsWith',
  'then',
  'toLowerCase',
  'toString',
  'toUpperCase',
  'trim',
  'values',
]);

/** The literal string arguments of a call, by name: `{ app: 'Avatars', item }` → `{ app }`. */
function literalArgs(text: string): Record<string, string> {
  const args: Record<string, string> = {};
  for (const match of text.matchAll(/([A-Za-z_$][\w$]*)\s*:\s*(['"`])((?:\\.|(?!\2)[^\\])*)\2/g)) {
    const value = match[3] as string;
    if (match[2] === '`' && value.includes('${')) continue;
    args[match[1] as string] ??= value.replace(/\\(.)/g, '$1');
  }
  return args;
}

/**
 * The operations a cell's code calls, in the order they run (a call runs once its arguments
 * have): `await avatars.avatar_list()`, `assistant.show({ app: 'Avatars' })`, the calls inside a
 * `Promise.all`. Not the language's own (`Promise.all`, `JSON.stringify`), nor methods of the
 * code's own values (`list.map`). Code that cannot be read gives none.
 */
export function parseCodeCalls(code: string): CodeCall[] {
  const masked = maskCode(code);
  const declared = declaredNames(masked);
  const calls: { call: CodeCall; end: number }[] = [];
  const pattern = /(?<![\w$.\])])([A-Za-z_$][\w$]*)\s*\.\s*([A-Za-z_$][\w$]*)\s*\(/g;
  for (const match of masked.matchAll(pattern)) {
    const namespace = match[1] as string;
    const method = match[2] as string;
    if (BUILTINS.has(namespace) || declared.has(namespace) || VALUE_METHODS.has(method)) continue;
    const open = (match.index ?? 0) + match[0].length - 1;
    const close = closingBracket(masked, open);
    if (close === -1) continue;
    calls.push({
      call: { namespace, method, args: literalArgs(code.slice(open + 1, close)) },
      end: close,
    });
  }
  return calls.sort((left, right) => left.end - right.end).map((entry) => entry.call);
}

/** Verbs a tool's name says, as the step says them. */
const VERBS = new Set([
  'add',
  'answer',
  'apply',
  'call',
  'cancel',
  'check',
  'close',
  'copy',
  'create',
  'delete',
  'describe',
  'download',
  'edit',
  'export',
  'fetch',
  'find',
  'follow',
  'generate',
  'get',
  'import',
  'install',
  'list',
  'make',
  'move',
  'open',
  'pause',
  'play',
  'preview',
  'publish',
  'query',
  'read',
  'record',
  'remove',
  'rename',
  'render',
  'reset',
  'run',
  'save',
  'schedule',
  'search',
  'select',
  'send',
  'set',
  'show',
  'start',
  'stop',
  'update',
  'upload',
  'view',
]);

const READ_VERBS = new Set([
  'check',
  'describe',
  'fetch',
  'find',
  'get',
  'list',
  'preview',
  'query',
  'read',
  'search',
  'view',
]);

const SHOW_VERBS = new Set(['show', 'open']);

function words(identifier: string): string[] {
  return identifier
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .split(/[_\-\s]+/)
    .map((word) => word.toLowerCase())
    .filter(Boolean);
}

/** The verb of a tool's name and the words it acts on: `avatar_photo_generate` → generate. */
function verbOf(method: string): { verb: string | null; object: string[] } {
  const parts = words(method);
  const index = VERBS.has(parts[0] ?? '')
    ? 0
    : VERBS.has(parts.at(-1) ?? '')
      ? parts.length - 1
      : parts.findIndex((word) => VERBS.has(word));
  if (index === -1) return { verb: null, object: parts };
  return { verb: parts[index] as string, object: parts.filter((_, at) => at !== index) };
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const plural = (word: string) => (word.endsWith('s') ? word : `${word}s`);

function isBrowserCall(call: CodeCall): boolean {
  return call.method === 'js' && /browser/i.test(call.namespace);
}

/**
 * What an operation does, in a few words: its server's own title, else what its name and literal
 * arguments say ("List avatars", "Generate avatar photo", "Show Avatars · Donald Trump"), else its
 * name (`avatars.avatar_creation`).
 */
export function codeCallTitle(call: CodeCall): string {
  if (call.title !== undefined && call.title.trim() !== '') return call.title.trim();
  if (isBrowserCall(call)) return call.args['title']?.trim() || 'Use the browser';
  const { verb, object } = verbOf(call.method);
  if (verb !== null && SHOW_VERBS.has(verb)) {
    const file = call.args['file'];
    const target =
      call.args['app'] ??
      (file === undefined ? undefined : basename(file)) ??
      call.args['conversation'] ??
      call.args['title'] ??
      (object.length > 0 ? object.join(' ') : undefined);
    const item = call.args['item'];
    if (target === undefined) return `${capitalize(verb)} on screen`;
    return item === undefined
      ? `${capitalize(verb)} ${target}`
      : `${capitalize(verb)} ${target} · ${item}`;
  }
  if (verb === null) {
    if (object.at(-1) === 'status' && object.length > 1)
      return `Check ${object.slice(0, -1).join(' ')} status`;
    return `${call.namespace}.${call.method}`;
  }
  const subject = object.length > 0 ? object : words(call.namespace);
  if (subject.length === 0) return capitalize(verb);
  const named =
    verb === 'list' ? [...subject.slice(0, -1), plural(subject.at(-1) as string)] : subject;
  return `${capitalize(verb)} ${named.join(' ')}`;
}

/** What an operation does, as the steps count it: a read, a show, the browser, or an action. */
export function codeCallKind(call: CodeCall): CodeCallKind {
  if (isBrowserCall(call)) return 'browser';
  const { verb } = verbOf(call.method);
  if (verb !== null && SHOW_VERBS.has(verb)) return 'show';
  if (call.readOnly === true) return 'read';
  if (verb !== null && READ_VERBS.has(verb)) return 'read';
  if (verb === null && words(call.method).at(-1) === 'status') return 'read';
  return 'write';
}

/**
 * A cell named by its operations: one ("List avatars"), the same several times ("Generate avatar
 * photo ×3"), or several ("List avatars, then show Avatars"). Code that calls nothing readable
 * runs code.
 */
export function codeRunTitle(titles: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const title of titles) counts.set(title, (counts.get(title) ?? 0) + 1);
  const named = [...counts].map(([title, count]) => (count > 1 ? `${title} ×${count}` : title));
  if (named.length === 0) return 'Run code';
  const [first, second] = named as [string, string | undefined];
  if (second === undefined) return first;
  const then = `${first}, then ${second.charAt(0).toLowerCase()}${second.slice(1)}`;
  return named.length === 2 ? then : `${then} and ${named.length - 2} more`;
}

/**
 * Each cell's operations, as code mode wrote them after it (by order: a cell's operations come
 * while it runs, before the next cell), and the operations so folded into a cell.
 */
export function codeRunOperations(parts: readonly CrowdexUIMessagePart[]): {
  byRun: Map<string, DynamicToolPart[]>;
  folded: Set<string>;
} {
  const byRun = new Map<string, DynamicToolPart[]>();
  const folded = new Set<string>();
  let run: string | null = null;
  for (const part of parts) {
    if (part.type !== 'dynamic-tool') continue;
    if (isCodeRunPart(part)) {
      run = part.toolCallId;
      if (!byRun.has(run)) byRun.set(run, []);
    } else if (run !== null && isCodeCallPart(part) && !folded.has(part.toolCallId)) {
      byRun.get(run)?.push(part);
      folded.add(part.toolCallId);
    }
  }
  return { byRun, folded };
}

/** A cell's operations: the steps code mode wrote for it, else the calls its code reads. */
function codeRunCalls(run: DynamicToolPart, operations: readonly DynamicToolPart[]): CodeCall[] {
  if (operations.length > 0) return operations.map(codeCallOfPart);
  const code = codeRunCode(run);
  return code === null ? [] : parseCodeCalls(code);
}

/** What a step's state reads: still running, waiting on the user, failed, or done. */
export type StepStatus = 'running' | 'waiting' | 'failed' | 'done';

function partStatus(part: DynamicToolPart): StepStatus {
  if (part.state === 'approval-requested') return 'waiting';
  if (part.state === 'input-streaming' || part.state === 'input-available') return 'running';
  if (part.state === 'output-error' || part.state === 'output-denied') return 'failed';
  return 'done';
}

/** What a cell gave back: its value, what it printed, or why it failed. */
type CodeRunResult = { value: string | null; output: string | null; error: string | null };

function resultText(raw: unknown): string | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'string') return raw;
  // Codex records its whole MCP call (`{ type: 'mcpToolCall', error, result }`): its result is
  // what the cell gave back.
  if (
    typeof raw === 'object' &&
    (raw as { type?: unknown }).type === 'mcpToolCall' &&
    'result' in raw &&
    (raw as { error?: unknown }).error == null
  )
    return resultText((raw as { result: unknown }).result);
  const content = Array.isArray(raw)
    ? raw
    : typeof raw === 'object' && Array.isArray((raw as { content?: unknown }).content)
      ? (raw as { content: unknown[] }).content
      : null;
  if (content !== null) {
    const texts = content.flatMap((item) =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as { text?: unknown }).text === 'string'
        ? [(item as { text: string }).text]
        : [],
    );
    if (texts.length > 0) return texts.join('\n');
  }
  return JSON.stringify(raw, null, 2);
}

const pretty = (value: unknown): string =>
  typeof value === 'string' ? value : (JSON.stringify(value, null, 2) ?? String(value));

function errorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return pretty(error);
}

/** A cell's result, as code mode returns it (`{ value }`, `{ error }`, `output`). */
function codeRunResult(part: DynamicToolPart): CodeRunResult | null {
  const failed = part.state === 'output-error' || part.state === 'output-denied';
  const text = resultText(
    part.state === 'output-error'
      ? part.errorText
      : part.state === 'output-available'
        ? part.output
        : undefined,
  );
  if (text === null) return failed ? { value: null, output: null, error: 'Failed' } : null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    ('value' in parsed || 'error' in parsed || 'output' in parsed)
  ) {
    const record = parsed as { value?: unknown; error?: unknown; output?: unknown };
    return {
      // A cell that returned nothing (`value: null`) shows no value.
      value: record.value == null ? null : pretty(record.value),
      output: typeof record.output === 'string' && record.output !== '' ? record.output : null,
      // `error: null` says there is none.
      error: record.error == null ? (failed ? 'Failed' : null) : errorMessage(record.error),
    };
  }
  return failed
    ? { value: null, output: null, error: text }
    : { value: text, output: null, error: null };
}

/** One operation of a cell, as its step shows it. */
type CodeOperation = {
  key: string;
  title: string;
  kind: CodeCallKind;
  status: StepStatus | null;
};

/** A cell (or an operation code mode wrote on its own), as one step. */
export type CodeStep = {
  title: string;
  status: StepStatus;
  code: string | null;
  operations: CodeOperation[];
  result: CodeRunResult | null;
  /** What it counts as in the turn's summary: one per operation, an action without any. */
  kinds: CodeCallKind[];
};

/** A cell's step: named by what it calls, true to its state, with its code and result. */
export function codeStep(part: DynamicToolPart, operations: readonly DynamicToolPart[]): CodeStep {
  const lone = isCodeCallPart(part) && !isCodeRunPart(part);
  const calls = lone ? [codeCallOfPart(part)] : codeRunCalls(part, operations);
  const statuses = operations.map(partStatus);
  const own = partStatus(part);
  const status: StepStatus =
    own === 'waiting' || statuses.includes('waiting')
      ? 'waiting'
      : own === 'running'
        ? 'running'
        : own === 'failed'
          ? 'failed'
          : 'done';
  const result = codeRunResult(part);
  const kinds = calls.map(codeCallKind);
  return {
    title: codeRunTitle(calls.map(codeCallTitle)),
    status: status === 'done' && result?.error != null ? 'failed' : status,
    code: lone ? null : codeRunCode(part),
    operations: calls.map((call, index) => ({
      key: operations[index]?.toolCallId ?? `call-${index}`,
      title: codeCallTitle(call),
      kind: kinds[index] as CodeCallKind,
      status: statuses[index] ?? null,
    })),
    result,
    kinds: kinds.length > 0 ? kinds : ['write'],
  };
}

/** The images a step's tool returned (a screenshot, a generated photo). */
export function stepImages(parts: readonly CrowdexUIMessagePart[], toolCallId: string): FilePart[] {
  return parts.filter(
    (part): part is FilePart => part.type === 'file' && toolImageCallId(part) === toolCallId,
  );
}
